import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
} from "@angular/core";
import { UserMaster } from "src/app/models/Masters/userMaster";
import { LoginService } from "src/app/services/login.service";
import { SharedService } from "src/app/services/shared.service";
import { RolePageResponse } from "src/app/models/Masters/roleMaster";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.css"],
})
export class SidebarComponent implements OnInit, OnDestroy {
  // ── existing state, untouched ────────────────────────────────────────────
  user: UserMaster;
  menu: any;
  mainMenuItems: any = [];
  subMenuItems: any = [];

  // ── branding: company logo, shown at the top of the panel ───────────────
  CompLogo: string = "";
  /** Shown when the logged-in company has no logo on record. */
  readonly fallbackLogo = "../../../assets/img/sureassure-logo.png";

  // ── presentation state ───────────────────────────────────────────────────
  // NOTE: `collapsed` and `mobileOpen` are getters further down, derived from
  // the template's own `.open` body class. They are deliberately not fields —
  // duplicating the state here is what caused TS2300.

  /** Groups the user has explicitly opened / explicitly shut. Tracked by index
   *  rather than by id because the menu payload's key field isn't part of any
   *  interface here — index is derived from the same *ngFor, so it can't drift. */
  private openGroups = new Set<number>();
  private closedGroups = new Set<number>();

  /** The template's own collapse/drawer class. */
  private static readonly OPEN_CLASS = "open";
  private static readonly MOBILE_MAX = 767;
  private static readonly TABLET_MAX = 1024;

  constructor(
    private loginService: LoginService,
    private sharedService: SharedService,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit() {
    this.user = this.loginService.user();
    this.loadCompanyBranding();
    this.measureShellInsets();
    this.centerToggle();

    this.getMenu({ compId: this.user.compId, roleId: this.user.roleId });
    this.sharedService.navbar.subscribe(() => {
      this.getMenu({ compId: this.user.compId, roleId: this.user.roleId });
    });
  }

  // ── branding ─────────────────────────────────────────────────────────────
  /**
   * Reads compLogo from the stored user, the same way the header used to -
   * parsed from localStorage rather than off `this.user` because UserMaster
   * doesn't declare this field.
   */
  private loadCompanyBranding(): void {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) {
        return;
      }
      const u = JSON.parse(raw);
      this.CompLogo = u.compLogo || "";
    } catch (e) {
      this.CompLogo = "";
    }
  }

  /** First word of the company name — the bold, prominent line of the
   *  brand plate (e.g. "BHARATI" of "Bharati Book Depot"). */
  get brandNamePrimary(): string {
    return ((this.user && this.user.compNm) || "").trim().split(/\s+/)[0] || "";
  }

  /** Everything after the first word — the lighter second line
   *  (e.g. "Book Depot"). Empty for a single-word company name. */
  get brandNameSecondary(): string {
    const parts = ((this.user && this.user.compNm) || "").trim().split(/\s+/);
    return parts.slice(1).join(" ");
  }


  get dashboardRoute(): string {
    const isLoyalty: any = this.user && (this.user as any).isLoyalty;
    const isLoyaltyEnabled =
      isLoyalty === true ||
      isLoyalty === "true" ||
      isLoyalty === "1" ||
      isLoyalty === 1;
    return isLoyaltyEnabled ? "/dashboard" : "/dashboard1";
  }

  ngOnDestroy(): void {
    // Never leave the drawer latched open if the shell tears this down.
    document.body.classList.remove("sb-drawer-open");
  }

  // ── collapse / expand ────────────────────────────────────────────────────
  /**
   * The Ela Admin template already owns this: a `.open` class re-flows
   * aside.left-panel to 83px AND .right-panel's margin-left to match.
   * `toggleCollapse()` below is this component's own trigger for it, so there
   * is exactly one class and one place that flips it, and the content offset
   * keeps working untouched.
   */
  get collapsed(): boolean {
    return document.body.classList.contains(SidebarComponent.OPEN_CLASS);
  }

  /**
   * Keeps the floating panel clear of the shell's fixed header and footer.
   * The Ela template puts its header at 55px and pins footer.site-footer to the
   * bottom, so rather than hardcode either: the footer is measured directly, and
   * the header falls back to the 69px default in the stylesheet.
   * Override --sb-inset-top / --sb-inset-bottom in global CSS to take control.
   */
  private measureShellInsets(): void {
    const host = this.hostRef && this.hostRef.nativeElement;
    if (!host) {
      return;
    }
    const vh = window.innerHeight;

    const footer = document.querySelector(
      "footer.site-footer, footer, #footer",
    ) as HTMLElement | null;
    if (footer) {
      const r = footer.getBoundingClientRect();
      const pinnedToBottom = r.bottom >= vh - 2;
      if (r.height > 0 && r.height < vh * 0.25 && pinnedToBottom) {
        host.style.setProperty(
          "--sb-inset-bottom",
          Math.round(r.height) + 14 + "px",
        );
      }
    }
  }

  /**
   * Centers the collapse toggle on the panel's own rendered height rather
   * than trusting `top: 50%` on its own. `.sb-toggle` is `position: fixed`,
   * whose containing block is normally the viewport - but the Ela template
   * sets `body { display: table; }` (style.css), and a handful of layout
   * quirks in this app can shift what a fixed element's "50%" actually
   * resolves against. Measuring #left-panel directly and writing its exact
   * pixel center into a custom property sidesteps all of that and keeps the
   * button pinned to the panel's true midpoint - see .sb-toggle's
   * `top: var(--sb-toggle-top, 50%)` in sidebar.component.css.
   */
  private centerToggle(): void {
    const host = this.hostRef && this.hostRef.nativeElement;
    const panel = document.getElementById("left-panel");
    if (!host || !panel) {
      return;
    }
    const rect = panel.getBoundingClientRect();
    if (rect.height > 0) {
      host.style.setProperty(
        "--sb-toggle-top",
        rect.top + rect.height / 2 + "px",
      );
    }
  }

  // ── mobile drawer ────────────────────────────────────────────────────────
  /** On phones the template reuses `.open` to mean "drawer showing". */
  get mobileOpen(): boolean {
    return window.innerWidth <= SidebarComponent.MOBILE_MAX && this.collapsed;
  }

  closeMobile(): void {
    document.body.classList.remove(SidebarComponent.OPEN_CLASS);
  }

  /** The panel's own collapse/expand trigger — the header's hamburger is gone,
   *  so this is now the only way to flip the template's `.open` class. */
  toggleCollapse(): void {
    document.body.classList.toggle(SidebarComponent.OPEN_CLASS);
  }

  /** Called after a submenu link is chosen: on phones the drawer should shut. */
  onNavigate(): void {
    if (window.innerWidth <= SidebarComponent.MOBILE_MAX) {
      this.closeMobile();
    }
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.mobileOpen) {
      this.closeMobile();
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    if (window.innerWidth > SidebarComponent.MOBILE_MAX && this.mobileOpen) {
      this.closeMobile();
    }
    this.measureShellInsets();
    this.centerToggle();
  }

  // ── submenu groups ───────────────────────────────────────────────────────
  /**
   * A group is open when the user opened it. When they haven't touched it, the
   * stylesheet still reveals the group containing the active route via
   * `:has(.sub-menu a.active)` — hence the separate "explicitly closed" set,
   * which lets a deliberate close win over that default.
   */
  isGroupOpen(i: number): boolean {
    return this.openGroups.has(i);
  }

  isGroupClosed(i: number): boolean {
    return this.closedGroups.has(i);
  }

  toggleGroup(i: number, ev: Event): void {
    ev.preventDefault(); // the toggler is an <a href="#">, don't jump
    ev.stopPropagation();

    // In the collapsed rail there is nowhere for a submenu to go, so the first
    // click expands the panel (by clearing the template's .open class) and
    // reveals the group in one motion. On phones .open means "drawer showing",
    // so it must be left alone there.
    if (this.collapsed && window.innerWidth > SidebarComponent.MOBILE_MAX) {
      document.body.classList.remove(SidebarComponent.OPEN_CLASS);
      this.openOnly(i);
      return;
    }

    if (this.openGroups.has(i)) {
      this.openGroups.delete(i);
      this.closedGroups.add(i); // beats the active-route default
    } else {
      this.openOnly(i);
    }
  }

 
  private openOnly(i: number): void {
    this.openGroups.clear();
    this.openGroups.add(i);
    this.closedGroups.clear();
    for (let j = 0; j < this.mainMenuItems.length; j++) {
      if (j !== i) {
        this.closedGroups.add(j);
      }
    }
  }

  // ── existing menu load, untouched ────────────────────────────────────────
  getMenu(obj: any) {
    this.loginService.getMenu(obj).subscribe(
      (rolePage: RolePageResponse) => {
        if (rolePage.responseCode === "200") {
          this.menu = rolePage;
          localStorage.menu = JSON.stringify(this.menu);
          this.mainMenuItems = this.menu.details.filter(
            (menu) => menu.parentId === 0,
          );
          this.subMenuItems = this.menu.details.filter(
            (menu) => menu.parentId !== 0,
          );
          // console.log(this.mainMenuItems);
        } else {
        }
      },
      (err) => {},
    );
  }
}
